import React, { useEffect, useState } from 'react';
import { LampCondition, Order, OrderCreateRequest, OrderPriority, OrderStatus } from '../../types/order';
import { orderService, bairroService } from '../../services/api';
import LocationPicker from '../../components/orders/LocationPicker';
import OrderReceiptPdfPreview from '../../components/orders/OrderReceiptPdfPreview';
import OrderTrackingModal from '../../components/orders/OrderTrackingModal';
import { buildOrderReceiptPdf } from '../../components/orders/orderReceiptPdf';
import { useAuth } from '../../contexts/AuthContext';
import { uploadPhoto, isImageFile, MAX_PHOTO_SIZE_BYTES } from '../../services/cloudinary';
import styles from './Orders.module.css';

const statusLabel: Record<OrderStatus, string> = {
  ABERTA: 'Aberta',
  AVULSA: 'Avulsa',
  EM_ANALISE: 'Em Análise',
  EM_ANDAMENTO: 'Em Andamento',
  PENDENTE: 'Pendente',
  CONCLUIDA: 'Concluída',
  EM_ATRASO: 'Em Atraso',
};

const priorityLabel: Record<OrderPriority, string> = {
  ALTA: 'Alta',
  MEDIA: 'Média',
  BAIXA: 'Baixa',
};

const statusColorClass: Record<OrderStatus, string> = {
  ABERTA: styles.badgeAberta,
  AVULSA: styles.badgeAvulsa,
  EM_ANALISE: styles.badgeEmAnalise,
  EM_ANDAMENTO: styles.badgeEmAndamento,
  PENDENTE: styles.badgePendente,
  CONCLUIDA: styles.badgeConcluida,
  EM_ATRASO: styles.badgeEmAtraso,
};

const priorityColorClass: Record<OrderPriority, string> = {
  ALTA: styles.badgeAlta,
  MEDIA: styles.badgeMedia,
  BAIXA: styles.badgeBaixa,
};

const PencilIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.actionIcon}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

const TrashIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.actionIcon}>
    <path d="M3 6h18" />
    <path d="M8 6V4h8v2" />
    <path d="M19 6l-1 14H6L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
  </svg>
);

const EraserIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.actionIcon}>
    <path d="M20 20H9" />
    <path d="M14.5 4.5 21 11a2.1 2.1 0 0 1 0 3L14 21H7L3 17a2.1 2.1 0 0 1 0-3l8.5-8.5a2.1 2.1 0 0 1 3 0Z" />
    <path d="m7 21 8.5-8.5" />
  </svg>
);

const PrintIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.actionIcon}>
    <polyline points="6 9 6 2 18 2 18 9" />
    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
    <rect x="6" y="14" width="12" height="8" />
  </svg>
);


const PhotoIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.actionIcon}>
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <path d="M21 15l-5-5L5 21" />
  </svg>
);


const HistoryIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.actionIcon}>
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const normalizeNeighborhoodValue = (value: string): string => value
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-zA-Z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '')
  .replace(/_+/g, '_')
  .toUpperCase();


const resolveNeighborhoodValue = (label: string, neighborhoods: { value: string; label: string }[]): string => {
  if (!label) return 'CENTRO';

  const byValue = neighborhoods.find(item => item.value === label);
  if (byValue) return byValue.value;

  const byLabel = neighborhoods.find(item => item.label === label);
  if (byLabel) return byLabel.value;

  return 'CENTRO';
};

const inferNeighborhoodFromAddress = (address: string | null | undefined): string | null => {
  if (!address) return null;

  const parts = address
    .split(' - ')
    .map(part => part.trim())
    .filter(Boolean);

  if (parts.length < 2) return null;
  return parts[1];
};

const fetchNeighborhoodFromCoords = async (latitude: number, longitude: number): Promise<string | null> => {
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&accept-language=pt-BR`;
    const response = await fetch(url, { headers: { 'User-Agent': 'crm-iluminacao-publica/1.0' } });
    if (!response.ok) return null;

    const data = await response.json();
    const address = data?.address || {};
    return (address.suburb || address.quarter || address.neighbourhood || null) as string | null;
  } catch {
    return null;
  }
};

const emptyForm: OrderCreateRequest = {
  address: '',
  neighborhood: 'CENTRO',
  reporterName: '',
  notes: '',
  poleBusbar: '',
  houseNumberInFront: '',
  referencePoint: '',
  latitude: null,
  longitude: null,
  lampCondition: 'APAGADA',
  issueReceipt: true,
  reporterPhotoUrl: null,
  status: 'ABERTA',
  priority: 'MEDIA',
};

const ITEMS_PER_PAGE = 10;

const Orders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterNeighborhood, setFilterNeighborhood] = useState('');
  const [filterProtocol, setFilterProtocol] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterPublicOnly, setFilterPublicOnly] = useState(false);
  const [filterCadastroCelpe, setFilterCadastroCelpe] = useState(false);
  const [filterMedida, setFilterMedida] = useState(false);
  const [filterEstimada, setFilterEstimada] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const [neighborhoodLabels, setNeighborhoodLabels] = useState<string[]>([
    'Aldeia Cadrogó', 'Aldeia Caípe', 'Aldeia Canaã', 'Aldeia Dois Irmãos',
    'Aldeia Lagoa', 'Aldeia Santana', 'Aldeia São José', 'Aldeia Varzinha',
    'Assent. Nossa Senhora', 'Baixa Grande', 'Beira Mar', 'Cacimbão',
    "Caixa D'Água", 'Caldeirão I', 'Caldeirão II', 'Caldeirão III',
    'Central', 'Centenário', 'Centro', 'Cimbres', 'Climério',
    'Cohab I', 'Cohab II', 'Eucalipto', 'Fazenda Rosário',
    'Freixeira Velha', 'Ipanema', 'Lagoa do Félix', 'Lot. Biá',
    'Lot. Gravatazinho', 'Lot. JK', 'Lot. José Gerônimo',
    'Lot. José Rocha', 'Lot. Leonardo Gomes', 'Lot. Miguel Lins',
    'Lot. Redenção', 'Milho Branco', 'Mimoso', 'Mutuca',
    'Novo Cajueiro', 'Papagaio de Baixo', 'Papagaio de Cima',
    'Papagaio do Meio', 'Pedra Redonda', 'Pintada', 'Pitanga',
    'Pitanguinha', 'Portal', 'Prado', 'Riacho Fundo', 'Salgado',
    'Salobro', 'Santo Antônio', 'São Sebastião', 'Serra da Cruz',
    'Serrinha', 'T.Q. Negros do Osso', 'Triângulo I', 'Triângulo II',
    'Triângulo III', 'Vira Kopus', 'Vila Anápolis',
    'Vila do Presídio', 'Xucurus',
  ]);

  const neighborhoods = neighborhoodLabels.map(label => ({
    value: normalizeNeighborhoodValue(label),
    label,
  }));

  const [showModal, setShowModal] = useState(false);
  const [modalStep, setModalStep] = useState<1 | 2 | 3>(1);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [savedOrder, setSavedOrder] = useState<Order | null>(null);
  const [form, setForm] = useState<OrderCreateRequest>(emptyForm);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [trackingOrder, setTrackingOrder] = useState<Order | null>(null);
  const [busbarConflict, setBusbarConflict] = useState<{ busbar: string; protocolNumber: string; status: string } | null>(null);
  const [noBusbar, setNoBusbar] = useState(false);
  const [photoOrder, setPhotoOrder] = useState<Order | null>(null);
  const [photoZoomed, setPhotoZoomed] = useState(false);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const photoInputRef = React.useRef<HTMLInputElement | null>(null);
  const busbarTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const { user } = useAuth();
  const isMaster = (user?.role || '').toUpperCase() === 'MASTER';

  const fetchOrders = () => {
    setLoading(true);
    setError('');
    const params: Record<string, string> = {};
    if (filterCadastroCelpe) params.cadastroCelpe = 'true';
    if (filterMedida) params.medida = 'true';
    if (filterEstimada) params.estimada = 'true';
    if (filterPublicOnly) params.public = 'true';
    const hasBooleanFilter = filterCadastroCelpe || filterMedida || filterEstimada || filterPublicOnly;
    (hasBooleanFilter ? orderService.getAllPages(params) : orderService.getOpenPages(params))
      .then(setOrders)
      .catch(() => setError('Erro ao carregar ordens de serviço'))
      .finally(() => setLoading(false));
  };

  const fetchAllOrders = () => {
    setLoading(true);
    setError('');
    const params: Record<string, string> = {};
    if (filterCadastroCelpe) params.cadastroCelpe = 'true';
    if (filterMedida) params.medida = 'true';
    if (filterEstimada) params.estimada = 'true';
    if (filterPublicOnly) params.public = 'true';
    orderService.getAllPages(params)
      .then(setOrders)
      .catch(() => setError('Erro ao carregar ordens de serviço'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchOrders();
  }, [filterPublicOnly]);

  useEffect(() => {
    bairroService.list()
      .then(res => setNeighborhoodLabels(res.data.map(b => b.nome)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, filterPriority, filterNeighborhood, filterProtocol, filterDateFrom, filterDateTo, filterPublicOnly, filterCadastroCelpe, filterMedida, filterEstimada]);

  useEffect(() => {
    fetchOrders();
  }, [filterCadastroCelpe, filterMedida, filterEstimada]);

  const handleFilterPublicOnlyChange = (checked: boolean) => {
    setFilterPublicOnly(checked);
  };

  const filteredOrders = orders.filter(o => {
    if (filterStatus && filterStatus !== 'TODAS' && o.status !== filterStatus) return false;
    if (filterPriority && o.priority !== filterPriority) return false;
    if (filterNeighborhood && resolveNeighborhoodValue(o.neighborhood ?? '', neighborhoods) !== filterNeighborhood) return false;
    if (filterProtocol && !(o.protocolNumber ?? '').toLowerCase().includes(filterProtocol.toLowerCase()) && !(o.poleBusbar ?? '').toLowerCase().includes(filterProtocol.toLowerCase())) return false;
    if (filterDateFrom || filterDateTo) {
      const orderDate = o.createdAt.slice(0, 10); // 'YYYY-MM-DD'
      if (filterDateFrom && orderDate < filterDateFrom) return false;
      if (filterDateTo && orderDate > filterDateTo) return false;
    }
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / ITEMS_PER_PAGE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * ITEMS_PER_PAGE;
  const paginatedOrders = filteredOrders.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  // Janela de páginas vizinhas (até 3 quadrados ao redor da página atual)
  const windowStart = Math.max(1, Math.min(safeCurrentPage - 1, totalPages - 2));
  const windowEnd = Math.min(totalPages, windowStart + 2);
  const visiblePages = Array.from(
    { length: Math.max(0, windowEnd - windowStart + 1) },
    (_, index) => windowStart + index,
  );

  const [pageInput, setPageInput] = useState('');

  const goToPage = () => {
    const target = parseInt(pageInput, 10);
    if (!Number.isNaN(target) && target >= 1 && target <= totalPages) {
      setCurrentPage(target);
    }
    setPageInput('');
  };

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const openCreate = () => {
    setEditingOrder(null);
    setSavedOrder(null);
    setForm(emptyForm);
    setModalStep(1);
    setFormError('');
    setBusbarConflict(null);
    setNoBusbar(false);
    resetPhotoState();
    setShowModal(true);
  };

  const resetPhotoState = () => {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(null);
    setPhotoPreview(null);
    setPhotoUploading(false);
  };

  const openEdit = (order: Order) => {
    setEditingOrder(order);
    setSavedOrder(order);
    setForm({
      description: order.description || '',
      address: order.address,
      neighborhood: resolveNeighborhoodValue(order.neighborhood ?? '', neighborhoods),
      reporterName: order.reporterName || '',
      notes: order.notes || '',
      poleBusbar: order.poleBusbar || '',
      houseNumberInFront: order.houseNumberInFront || '',
      referencePoint: order.referencePoint || '',
      latitude: order.latitude,
      longitude: order.longitude,
      lampCondition: order.lampCondition || 'APAGADA',
      issueReceipt: order.receiptPrinted ?? true,
      status: order.status,
      priority: order.priority,
      reporterPhotoUrl: order.reporterPhotoUrl ?? null,
    });
    setNoBusbar(order.poleBusbar === 'S/B' || !order.poleBusbar?.trim());
    setPhotoFile(null);
    setPhotoPreview(order.reporterPhotoUrl ?? null);
    setPhotoUploading(false);
    setModalStep(1);
    setFormError('');
    setShowModal(true);
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!isImageFile(file)) {
      setFormError('O arquivo selecionado não é uma imagem.');
      return;
    }
    if (file.size > MAX_PHOTO_SIZE_BYTES) {
      setFormError('A foto deve ter no máximo 5 MB.');
      return;
    }
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
    setFormError('');
  };

  const removePhoto = () => {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(null);
    setPhotoPreview(null);
    setForm(f => ({ ...f, reporterPhotoUrl: null }));
  };



  const validateStepOne = (): boolean => {
    if (!form.reporterName?.trim()) { setFormError('Solicitante é obrigatório'); return false; }
    if (!form.address.trim()) { setFormError('Endereço é obrigatório'); return false; }
    if (!form.neighborhood?.trim()) { setFormError('Bairro é obrigatório'); return false; }
    if (!noBusbar && form.poleBusbar?.trim() && form.poleBusbar !== 'S/B' && !/^[A-Z]\d{1,7}$/.test(form.poleBusbar.trim())) {
      setFormError('Barramento deve seguir o formato de uma letra (A-Z) + 1 a 7 números (ex: A1234567)'); return false;
    }
    return true;
  };

  const goToLocationStep = () => {
    setFormError('');
    if (!validateStepOne()) return;
    setModalStep(2);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalStep(1);
    setSavedOrder(null);
    setFormError('');
    resetPhotoState();
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Confirma a exclusão desta ordem de serviço?')) return;
    try {
      await orderService.delete(id);
      setOrders(prev => prev.filter(o => o.id !== id));
    } catch {
      alert('Erro ao excluir ordem de serviço');
    }
  };

  const openReceiptForPrint = async (order: Order) => {
    try {
      const document = await buildOrderReceiptPdf(
        {
          protocolNumber: order.protocolNumber,
          reporterName: order.reporterName || '',
          address: order.address,
          neighborhood: order.neighborhood || '',
          houseNumberInFront: order.houseNumberInFront || '',
          referencePoint: order.referencePoint || '',
          notes: order.notes || '',
          lampCondition: order.lampCondition || undefined,
          priority: order.priority,
          status: order.status,
        },
        new Date(),
      );
      const blobUrl = URL.createObjectURL(document.output('blob'));
      const printWindow = window.open(blobUrl, '_blank');

      if (!printWindow) {
        URL.revokeObjectURL(blobUrl);
        alert('Nao foi possivel abrir a janela de impressao. Verifique se o navegador bloqueou pop-ups.');
        return;
      }

      printWindow.addEventListener('load', () => {
        setTimeout(() => {
          printWindow.print();
        }, 150);
      });
      printWindow.addEventListener('afterprint', () => {
        URL.revokeObjectURL(blobUrl);
      });
      printWindow.addEventListener('beforeunload', () => {
        URL.revokeObjectURL(blobUrl);
      });

      const updatedOrder = await orderService.markReceiptPrinted(order.id);
      setOrders(prev => prev.map(currentOrder => currentOrder.id === updatedOrder.id ? updatedOrder : currentOrder));
      if (editingOrder?.id === updatedOrder.id) {
        setEditingOrder(updatedOrder);
      }
      if (savedOrder?.id === updatedOrder.id) {
        setSavedOrder(updatedOrder);
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erro ao gerar comprovante para impressao');
    }
  };

  const handleSave = async () => {
    if (modalStep !== 2 && !(editingOrder && modalStep === 1)) {
      return;
    }

    setFormError('');
    if (!validateStepOne()) return;
    if (modalStep === 2 && (form.latitude == null || form.longitude == null)) {
      setFormError('Marque a localização no mapa antes de salvar'); return;
    }

    setSubmitting(true);
    try {
      let reporterPhotoUrl = form.reporterPhotoUrl ?? null;

      if (photoFile) {
        setPhotoUploading(true);
        try {
          reporterPhotoUrl = await uploadPhoto(photoFile);
        } catch (uploadErr: unknown) {
          setFormError(uploadErr instanceof Error ? uploadErr.message : 'Falha ao enviar a foto.');
          return;
        } finally {
          setPhotoUploading(false);
        }
      }

      const payload = { ...form, reporterPhotoUrl };

      let persistedOrder: Order;

      if (editingOrder) {
        persistedOrder = await orderService.update(editingOrder.id, payload);
        setOrders(prev => prev.map(o => o.id === persistedOrder.id ? persistedOrder : o));
      } else {
        persistedOrder = await orderService.create(payload);
        setOrders(prev => [persistedOrder, ...prev]);
      }

      setEditingOrder(persistedOrder);
      setSavedOrder(persistedOrder);

      if (form.issueReceipt === false || modalStep === 1) {
        closeModal();
        return;
      }

      setModalStep(3);
    } catch (err: any) {
      if (err.statusCode === 409) {
        const protocol = err.protocolNumber || 'desconhecido';
        setFormError(`Já existe uma ordem de serviço não concluída com este barramento. Protocolo: ${protocol}`);
        return;
      }
      setFormError(err instanceof Error ? err.message : 'Erro ao salvar ordem');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedNeighborhoodLabel = neighborhoods.find(
    neighborhood => neighborhood.value === form.neighborhood,
  )?.label;

  const locationSearchAddress = [
    form.address,
    selectedNeighborhoodLabel,
    'Pesqueira - PE',
  ]
    .filter((item): item is string => Boolean(item && item.trim()))
    .join(', ');

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Ordens de Serviço</h1>
          <p className={styles.subtitle}>Visualize e gerencie todas as ordens de serviço</p>
        </div>
        <button className={styles.newOrderButton} onClick={openCreate}>
          + Nova OS
        </button>
      </div>

      <div className={styles.filtersSection}>
        <div className={styles.filtersContainer}>
          <input
            className={styles.filterSelect}
            type="text"
            placeholder="Buscar por protocolo ou barramento..."
            value={filterProtocol}
            onChange={e => setFilterProtocol(e.target.value)}
          />
          <select className={styles.filterSelect} value={filterStatus} onChange={e => {
            const v = e.target.value;
            setFilterStatus(v);
            if (v === 'TODAS') {
              setFilterPublicOnly(false);
              fetchAllOrders();
            } else if (v === '') {
              fetchOrders();
            } else {
              setFilterPublicOnly(false);
              fetchAllOrders();
            }
          }}>
            <option value="">Não concluídas</option>
            <option value="TODAS">Todas</option>
            <option value="ABERTA">Aberta</option>
            <option value="AVULSA">Avulsa</option>
            <option value="EM_ANDAMENTO">Em Andamento</option>
            <option value="PENDENTE">Pendente</option>
            <option value="CONCLUIDA">Concluída</option>
            <option value="EM_ATRASO">Em Atraso</option>
          </select>

          <select className={styles.filterSelect} value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
            <option value="">Todas as Prioridades</option>
            <option value="ALTA">Alta</option>
            <option value="MEDIA">Média</option>
            <option value="BAIXA">Baixa</option>
          </select>

          <select className={styles.filterSelect} value={filterNeighborhood} onChange={e => setFilterNeighborhood(e.target.value)}>
            <option value="">Todos os Bairros</option>
            {neighborhoods.map(neighborhood => (
              <option key={neighborhood.value} value={neighborhood.value}>{neighborhood.label}</option>
            ))}
          </select>

          <input
            className={styles.filterDate}
            type="date"
            title="Data inicial"
            value={filterDateFrom}
            onChange={e => setFilterDateFrom(e.target.value)}
          />
          <input
            className={styles.filterDate}
            type="date"
            title="Data final"
            value={filterDateTo}
            onChange={e => setFilterDateTo(e.target.value)}
          />

          <label className={styles.filterCheckbox} title="Filtrar apenas ordens abertas pelo portal público">
            <input
              type="checkbox"
              checked={filterPublicOnly}
              onChange={e => handleFilterPublicOnlyChange(e.target.checked)}
            />
            <span>Público</span>
          </label>

          <label className={styles.filterCheckbox} title="Filtrar por Cadastro Celpe">
            <input
              type="checkbox"
              checked={filterCadastroCelpe}
              onChange={e => setFilterCadastroCelpe(e.target.checked)}
            />
            <span>Cadastro Celpe</span>
          </label>
          <label className={styles.filterCheckbox} title="Filtrar por Medida">
            <input
              type="checkbox"
              checked={filterMedida}
              onChange={e => setFilterMedida(e.target.checked)}
            />
            <span>Medida</span>
          </label>
          <label className={styles.filterCheckbox} title="Filtrar por Estimada">
            <input
              type="checkbox"
              checked={filterEstimada}
              onChange={e => setFilterEstimada(e.target.checked)}
            />
            <span>Estimada</span>
          </label>

          {(filterStatus || filterPriority || filterNeighborhood || filterProtocol || filterDateFrom || filterDateTo || filterPublicOnly || filterCadastroCelpe || filterMedida || filterEstimada) && (
            <button
              className={styles.applyButton}
               onClick={() => { setFilterStatus(''); setFilterPriority(''); setFilterNeighborhood(''); setFilterProtocol(''); setFilterDateFrom(''); setFilterDateTo(''); setFilterPublicOnly(false); setFilterCadastroCelpe(false); setFilterMedida(false); setFilterEstimada(false); fetchOrders(); }}
              aria-label="Limpar filtros"
              title="Limpar filtros"
            >
              <EraserIcon />
            </button>
          )}
        </div>
      </div>

      {error && <p className={styles.errorText}>{error}</p>}

      {loading ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateContent}>
            <p>Carregando...</p>
          </div>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateContent}>
            <div className={styles.emptyIcon}>📋</div>
            <h3 className={styles.emptyTitle}>Nenhuma ordem de serviço encontrada</h3>
            <p className={styles.emptyDescription}>
              {filterStatus || filterPriority || filterNeighborhood || filterProtocol || filterDateFrom || filterDateTo || filterPublicOnly
                ? 'Nenhuma ordem corresponde aos filtros selecionados.'
                : 'Não há ordens de serviço cadastradas.'}
            </p>
          </div>
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Protocolo</th>
                <th>Endereço</th>
                <th>Bairro</th>
                <th>Status</th>
                <th>Prioridade</th>
                <th>Solicitante</th>
                <th>Comprovante</th>
                <th>Data</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {paginatedOrders.map(order => (
                <tr key={order.id}>
                  <td data-label="Protocolo">{order.protocolNumber || '-'}</td>
                  <td data-label="Endereço">{order.address}</td>
                  <td data-label="Bairro">{order.neighborhood || '-'}</td>
                  <td data-label="Status">
                    <span className={`${styles.badge} ${statusColorClass[order.status]}`}>
                      {statusLabel[order.status]}
                    </span>
                    {order.origin === 'PORTAL_PUBLICO' && (
                      <span className={`${styles.badge} ${styles.badgePublic}`} style={{ marginLeft: 4 }}>
                        Público
                      </span>
                    )}
                  </td>
                  <td data-label="Prioridade">
                    <span className={`${styles.badge} ${priorityColorClass[order.priority]}`}>
                      {priorityLabel[order.priority]}
                    </span>
                  </td>
                  <td data-label="Solicitante">{order.reporterName || '-'}</td>
                  <td data-label="Comprovante">{order.receiptPrinted ? 'Impresso' : 'Não impresso'}</td>
                  <td data-label="Data">{new Date(order.createdAt).toLocaleDateString('pt-BR')}</td>
                  <td data-label="Ações">
                    <button
                      className={`${styles.editButton} ${order.status === 'CONCLUIDA' ? styles.actionDisabled : ''}`}
                      onClick={() => openEdit(order)}
                      disabled={order.status === 'CONCLUIDA'}
                      aria-label="Editar ordem"
                      title={order.status === 'CONCLUIDA' ? 'Ordem concluída não pode ser editada' : 'Editar ordem'}
                    >
                      <PencilIcon />
                    </button>
                    <button className={styles.printButton} onClick={() => openReceiptForPrint(order)} aria-label="Imprimir comprovante" title="Imprimir comprovante">
                      <PrintIcon />
                    </button>
                                        {order.reporterPhotoUrl && (
                      <button className={styles.printButton} onClick={() => { setPhotoOrder(order); setPhotoZoomed(false); }} aria-label="Ver foto enviada" title="Ver foto enviada pelo munícipe">
                        <PhotoIcon />
                      </button>
                    )}
                    
<button className={styles.trackingButton} onClick={() => setTrackingOrder(order)} aria-label="Acompanhar ordem" title="Acompanhamento da OS">
                      <HistoryIcon />
                    </button>
                    {isMaster ? (
                      <button
                        className={`${styles.deleteButton} ${order.status === 'CONCLUIDA' ? styles.actionDisabled : ''}`}
                        onClick={() => handleDelete(order.id)}
                        disabled={order.status === 'CONCLUIDA'}
                        aria-label="Excluir ordem"
                        title={order.status === 'CONCLUIDA' ? 'Ordem concluída não pode ser excluída' : 'Excluir ordem'}
                      >
                        <TrashIcon />
                      </button>
                    ) : (
                      <button
                        className={`${styles.inactivateButton} ${order.status === 'CONCLUIDA' ? styles.actionDisabled : ''}`}
                        onClick={() => handleDelete(order.id)}
                        disabled={order.status === 'CONCLUIDA'}
                        aria-label="Inativar ordem"
                        title={order.status === 'CONCLUIDA' ? 'Ordem concluída não pode ser inativada' : 'Inativar ordem'}
                      >
                        <TrashIcon />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredOrders.length > ITEMS_PER_PAGE && (
            <div className={styles.pagination}>
              <span className={styles.paginationInfo}>
                Mostrando {startIndex + 1}-{Math.min(startIndex + ITEMS_PER_PAGE, filteredOrders.length)} de {filteredOrders.length}
              </span>
              <div className={styles.paginationControls}>
                <button
                  className={styles.paginationButton}
                  onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
                  disabled={safeCurrentPage === 1}
                >
                  Anterior
                </button>
                {visiblePages.map(pageNumber => (
                  <button
                    key={pageNumber}
                    className={`${styles.paginationButton} ${pageNumber === safeCurrentPage ? styles.paginationButtonActive : ''}`}
                    onClick={() => setCurrentPage(pageNumber)}
                  >
                    {pageNumber}
                  </button>
                ))}
                <button
                  className={styles.paginationButton}
                  onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
                  disabled={safeCurrentPage === totalPages}
                >
                  Próxima
                </button>
              </div>
              <div className={styles.paginationJump}>
                <span className={styles.paginationJumpLabel}>Ir para a página</span>
                <input
                  type="number"
                  min={1}
                  max={totalPages}
                  className={styles.paginationJumpInput}
                  value={pageInput}
                  onChange={(event) => setPageInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') goToPage();
                  }}
                  placeholder={`1-${totalPages}`}
                />
                <button
                  type="button"
                  className={styles.paginationJumpButton}
                  onClick={goToPage}
                >
                  Ir
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {trackingOrder && (
        <OrderTrackingModal
          order={trackingOrder}
          onClose={() => setTrackingOrder(null)}
        />
      )}

      {photoOrder && photoOrder.reporterPhotoUrl && (
        <div className={styles.modalOverlay} onClick={() => setPhotoOrder(null)}>
          <div className={`${styles.modal} ${styles.photoModal}`} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                Foto enviada — {photoOrder.protocolNumber || `OS #${photoOrder.id}`}
              </h3>
              <button className={styles.modalCloseBtn} onClick={() => setPhotoOrder(null)} aria-label="Fechar">×</button>
            </div>
            <div className={`${styles.photoViewer} ${photoZoomed ? styles.photoViewerZoomed : ''}`}>
              <img
                src={photoOrder.reporterPhotoUrl}
                alt="Foto enviada pelo munícipe"
                className={`${styles.photoImage} ${photoZoomed ? styles.photoImageZoomed : ''}`}
                onClick={() => setPhotoZoomed(z => !z)}
              />
            </div>
            <p className={styles.photoHint}>Clique na imagem para {photoZoomed ? 'reduzir' : 'ampliar (zoom)'}.</p>
          </div>
        </div>
      )}

      {showModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                {editingOrder ? 'Editar Ordem de Serviço' : 'Nova Ordem de Serviço'}
              </h3>
              <button className={styles.modalCloseBtn} onClick={closeModal} aria-label="Fechar">×</button>
            </div>
            <div className={styles.modalSteps}>
              <div className={`${styles.modalStepItem} ${modalStep === 1 ? styles.modalStepItemActive : ''}`}>
                1. Dados gerais
              </div>
              <div className={`${styles.modalStepItem} ${modalStep === 2 ? styles.modalStepItemActive : ''}`}>
                2. Localizacao
              </div>
              <div className={`${styles.modalStepItem} ${modalStep === 3 ? styles.modalStepItemActive : ''}`}>
                3. Comprovante PDF
              </div>
            </div>
            <form onSubmit={(e) => e.preventDefault()}>
              {modalStep === 1 && (
                <>
                  <div className={styles.formRow}>
                    <div className={`${styles.formGroup} ${styles.formGroupBusbar}`}>
                      <label>Barramento do Poste</label>
                      <input
                        className={styles.formInput}
                        type="text"
                        value={form.poleBusbar || ''}
                        disabled={noBusbar}
                        onChange={e => {
                          let value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                          value = value.replace(/^\d+/, '');
                          value = value.replace(/^([A-Z]).*$/, (_, c) => c + value.slice(1).replace(/[A-Z]/g, ''));
                          value = value.slice(0, 8);
                          setForm(f => ({ ...f, poleBusbar: value }));
                          setBusbarConflict(null);
                          if (busbarTimerRef.current) clearTimeout(busbarTimerRef.current);
                          if (value.trim() && value !== 'S/B') {
                            const requestedBusbar = value.trim().toUpperCase();
                            busbarTimerRef.current = setTimeout(async () => {
                              const conflict = await orderService.checkBusbarConflict(requestedBusbar);
                              setForm(current => {
                                const currentBusbar = current.poleBusbar?.trim().toUpperCase() || '';
                                if (currentBusbar !== requestedBusbar) return current;
                                return current;
                              });
                              if (conflict.conflict) {
                                setBusbarConflict({
                                  busbar: requestedBusbar,
                                  protocolNumber: conflict.protocolNumber || 'desconhecido',
                                  status: conflict.status || '',
                                });
                                return;
                              }
                              setBusbarConflict(null);

                              const loc = await orderService.getPoleBusbarLocation(requestedBusbar);
                              const hasValidCoords =
                                !!loc
                                && Number.isFinite(loc.latitude)
                                && Number.isFinite(loc.longitude);

                              if (!hasValidCoords) {
                                setForm(current => {
                                  const currentBusbar = current.poleBusbar?.trim().toUpperCase() || '';
                                  if (currentBusbar !== requestedBusbar) {
                                    return current;
                                  }
                                  return {
                                    ...current,
                                    address: '',
                                    latitude: null,
                                    longitude: null,
                                  };
                                });
                                return;
                              }

                              const resolvedAddress = loc.address || '';
                              const rawNeighborhood = loc.neighborhood
                                || inferNeighborhoodFromAddress(loc.address)
                                || await fetchNeighborhoodFromCoords(loc.latitude, loc.longitude);

                              setForm(current => {
                                const currentBusbar = current.poleBusbar?.trim().toUpperCase() || '';
                                if (currentBusbar !== requestedBusbar) {
                                  return current;
                                }
                                return {
                                  ...current,
                                  address: resolvedAddress || '',
                                  latitude: loc.latitude,
                                  longitude: loc.longitude,
                                  ...(rawNeighborhood ? { neighborhood: resolveNeighborhoodValue(rawNeighborhood, neighborhoods) } : {}),
                                };
                              });
                            }, 600);
                          }
                        }}
                      />
                      <label className={styles.publicCheckbox} style={{ marginTop: 6 }}>
                        <input type="checkbox" checked={noBusbar}
                          onChange={(e) => {
                            setNoBusbar(e.target.checked);
                            setForm(f => ({ ...f, poleBusbar: e.target.checked ? 'S/B' : '' }));
                            setBusbarConflict(null);
                          }} />
                        Não possui barramento (S/B)
                      </label>
                      {busbarConflict && (
                        <div style={{
                          marginTop: 6,
                          padding: '6px 10px',
                          backgroundColor: '#fff4e6',
                          border: '1px solid #ffc078',
                          borderRadius: 6,
                          fontSize: 12,
                          color: '#d9480f',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                        }}>
                          <span style={{ flex: '1 1 auto', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            ⚠️ {busbarConflict.busbar}: {busbarConflict.protocolNumber}
                            {busbarConflict.status ? ` (${busbarConflict.status === 'EM_ANDAMENTO' ? 'Em andamento' : busbarConflict.status === 'PENDENTE' ? 'Pendente' : busbarConflict.status === 'ABERTA' ? 'Aberta' : busbarConflict.status})` : ''}
                          </span>
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                await navigator.clipboard.writeText(busbarConflict.protocolNumber);
                              } catch { /* ignore */ }
                            }}
                            style={{
                              flexShrink: 0,
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '2px 6px',
                              borderRadius: 4,
                              color: '#d9480f',
                              fontSize: 12,
                              fontWeight: 600,
                            }}
                            title="Copiar protocolo"
                          >
                            📋
                          </button>
                        </div>
                      )}
                    </div>
                    <div className={`${styles.formGroup} ${styles.formGroupReporter}`}>
                      <label>Solicitante *</label>
                      <input
                        className={styles.formInput}
                        type="text"
                        value={form.reporterName || ''}
                        onChange={e => setForm(f => ({ ...f, reporterName: e.target.value }))}
                        required
                      />
                    </div>
                  </div>
                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label>Endereço *</label>
                      <input
                        className={styles.formInput}
                        type="text"
                        value={form.address}
                        onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                        required
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label>Nº da Casa em Frente ao Poste</label>
                      <input
                        className={styles.formInput}
                        type="text"
                        value={form.houseNumberInFront || ''}
                        onChange={e => setForm(f => ({ ...f, houseNumberInFront: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label>Bairro *</label>
                      <select
                        className={styles.formSelect}
                        value={form.neighborhood || 'CENTRO'}
                        onChange={e => setForm(f => ({ ...f, neighborhood: e.target.value }))}
                        required
                      >
                        {neighborhoods.map(neighborhood => (
                          <option key={neighborhood.value} value={neighborhood.value}>{neighborhood.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className={styles.formGroup}>
                      <label>Status</label>
                      <select
                        className={styles.formSelect}
                        value={form.status}
                        onChange={e => setForm(f => ({ ...f, status: e.target.value as OrderStatus }))}
                      >
                        <option value="ABERTA">Aberta</option>
                        <option value="AVULSA">Avulsa</option>
                        <option value="EM_ANDAMENTO">Em Andamento</option>
                        <option value="PENDENTE">Pendente</option>
                        <option value="CONCLUIDA">Concluída</option>
                        <option value="EM_ATRASO">Em Atraso</option>
                      </select>
                    </div>
                    <div className={styles.formGroup}>
                      <label>Prioridade</label>
                      <select
                        className={styles.formSelect}
                        value={form.priority}
                        onChange={e => setForm(f => ({ ...f, priority: e.target.value as OrderPriority }))}
                      >
                        <option value="ALTA">Alta</option>
                        <option value="MEDIA">Média</option>
                        <option value="BAIXA">Baixa</option>
                      </select>
                    </div>
                  </div>
                  <div className={styles.formGroup}>
                    <label>Ponto de Referência</label>
                    <input
                      className={styles.formInput}
                      type="text"
                      value={form.referencePoint || ''}
                      onChange={e => setForm(f => ({ ...f, referencePoint: e.target.value }))}
                    />
                  </div>
                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label>Situação da Lâmpada</label>
                      <select
                        className={styles.formSelect}
                        value={form.lampCondition || 'APAGADA'}
                        onChange={e => setForm(f => ({ ...f, lampCondition: e.target.value as LampCondition }))}
                      >
                        <option value="APAGADA">Lâmpada apagada</option>
                        <option value="SEMPRE_ACESA">Sempre acesa</option>
                        <option value="INTERMITENTE">Intermitente</option>
                        <option value="INSTALACAO_KIT">Instalação de Kit</option>
                      </select>
                    </div>
                    <div className={styles.formGroup}>
                      <label>Emitir comprovante impresso?</label>
                      <select
                        className={styles.formSelect}
                        value={form.issueReceipt ? 'SIM' : 'NAO'}
                        onChange={e => setForm(f => ({ ...f, issueReceipt: e.target.value === 'SIM' }))}
                      >
                        <option value="SIM">Sim</option>
                        <option value="NAO">Nao</option>
                      </select>
                    </div>
                  </div>
                  <div className={styles.formGroup}>
                    <label>Observações</label>
                    <textarea
                      className={styles.formTextarea}
                      value={form.notes || ''}
                      onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                      rows={3}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label>Foto do poste <span style={{ fontWeight: 400, color: 'var(--color-text-light)' }}>(opcional)</span></label>
                    <input ref={photoInputRef} type="file" accept="image/*" hidden onChange={handlePhotoChange} />
                    {!photoPreview ? (
                      <button
                        type="button"
                        className={styles.photoUploadButton}
                        onClick={() => photoInputRef.current?.click()}
                        disabled={photoUploading}
                      >
                        {photoUploading ? 'Enviando foto...' : 'Adicionar foto'}
                      </button>
                    ) : (
                      <div className={styles.photoUploadPreview}>
                        <img src={photoPreview} alt="Prévia da foto do poste" />
                        <div className={styles.photoUploadActions}>
                          <button
                            type="button"
                            className={styles.photoUploadButton}
                            onClick={() => photoInputRef.current?.click()}
                            disabled={photoUploading}
                          >
                            Trocar
                          </button>
                          <button
                            type="button"
                            className={styles.photoUploadRemove}
                            onClick={removePhoto}
                            disabled={photoUploading}
                          >
                            Remover
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {modalStep === 2 && (
                <div className={styles.formGroup}>
                  <label>Localização no mapa *</label>
                  <LocationPicker
                    latitude={form.latitude}
                    longitude={form.longitude}
                    searchAddress={locationSearchAddress}
                    onChange={({ latitude, longitude, address, neighborhood }) => {
                      setForm(f => ({
                        ...f,
                        latitude,
                        longitude,
                        ...(address ? { address } : {}),
                        ...(neighborhood ? { neighborhood: resolveNeighborhoodValue(neighborhood, neighborhoods) } : {}),
                      }));
                    }}
                  />
                </div>
              )}
              {modalStep === 3 && savedOrder && (
                <OrderReceiptPdfPreview
                  protocolNumber={savedOrder.protocolNumber}
                  reporterName={savedOrder.reporterName || form.reporterName || ''}
                  address={savedOrder.address || form.address}
                  neighborhood={savedOrder.neighborhood || selectedNeighborhoodLabel || ''}
                  houseNumberInFront={savedOrder.houseNumberInFront || form.houseNumberInFront || ''}
                  referencePoint={savedOrder.referencePoint || form.referencePoint || ''}
                  notes={savedOrder.notes || form.notes || ''}
                  lampCondition={savedOrder.lampCondition || form.lampCondition}
                  priority={savedOrder.priority || form.priority}
                  status={savedOrder.status || form.status}
                  autoPrint={!editingOrder}
                />
              )}
              {formError && <p className={styles.errorText}>{formError}</p>}
              <div className={styles.modalActions}>
                {!(!editingOrder && modalStep === 3) && (
                  <button type="button" className={styles.cancelButton} onClick={closeModal}>
                    Cancelar
                  </button>
                )}
                {modalStep === 2 && (
                  <button
                    type="button"
                    className={styles.backButton}
                    onClick={() => setModalStep(1)}
                    disabled={submitting}
                  >
                    Voltar
                  </button>
                )}
                {modalStep === 1 ? (
                  <>
                    {editingOrder && (
                      <button type="button" className={styles.saveButton} onClick={handleSave} disabled={submitting}>
                        {submitting ? 'Salvando...' : 'Salvar'}
                      </button>
                    )}
                    <button type="button" className={styles.nextButton} onClick={goToLocationStep}>
                      Próximo
                    </button>
                  </>
                ) : modalStep === 2 ? (
                  <button type="button" className={styles.saveButton} onClick={handleSave} disabled={submitting}>
                    {submitting ? 'Salvando...' : (form.issueReceipt ? 'Salvar e imprimir' : 'Salvar')}
                  </button>
                ) : (
                  <button type="button" className={styles.saveButton} onClick={closeModal}>
                    Fechar
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;

